import { countryCode } from "@@/shared/countries";
import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type { Address} from "@@/shared/locations/address";
import { addressZodSchema } from "@@/shared/locations/address";
import { luhnCheck } from "@@/utilities/luhn-check";
import type { Except } from "type-fest";
import { z } from "zod";
import {
    companyDetailsBaseZodSchema,
    type _CompanyDetailsBase,
} from "./_company-details-base";
import { companyDetailsIdFactory } from "./company-details-id";

/**
 * Svensk betalningsmottagare
 */
type CompanyDetailsSwedish = _CompanyDetailsBase & {
    /**
     * What is the company called, sometimes differs from legal name.
     */
    readonly name: string;
    /**
     * E.g. "Företag AB"
     */
    readonly legalName: string;
    readonly address: Address;
    readonly countryCode: (typeof countryCode)["SE"];
    readonly organisationNumber: string;
} & (
        | {
              vatNo: string;
              isNotVatRegistered?: false;
          }
        | {
              vatNo?: undefined;
              isNotVatRegistered: true;
          }
    );

type FactoryParams = Except<
    CompanyDetailsSwedish,
    "_id" | "_type" | "_createdAt"
>;
const companyDetailsSwedishFactory = (params: FactoryParams) => {
    const command: CompanyDetailsSwedish = {
        ...params,
        _id: companyDetailsIdFactory(),
        _type: "COMPANY_DETAILS",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

const orgNrZodSchema = z
    .string()
    .transform((input: string) => input.replace(/\D/g, ""))
    .refine(value => value.length === 12 || value.length === 10, {
        message: "ogiltigt organisationsnummer, felaktigt antal siffror",
    })
    .refine(value => luhnCheck(value, true), {
        message: "ogiltigt organisationsnummer, felaktig kontrollsiffra",
    });

const companyDetailsSwedishZodSchema: z.ZodSchema<CompanyDetailsSwedish> =
    z.intersection(
        companyDetailsBaseZodSchema,
        z
            .object({
                legalName: z.string(),
                address: addressZodSchema,
                countryCode: z.literal(countryCode.SE),
                organisationNumber: orgNrZodSchema,
            })
            .and(
                z
                    .object({
                        vatNo: z.string(),
                        isNotVatRegistered: z.literal(false).optional(),
                    })
                    .or(
                        z.object({
                            vatNo: z.undefined(),
                            isNotVatRegistered: z.literal(true),
                        }),
                    ),
            ),
    );

export {
    companyDetailsSwedishFactory,
    companyDetailsSwedishZodSchema,
    orgNrZodSchema,
};
export type { CompanyDetailsSwedish };
